import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useIntl } from 'react-intl'
import * as Analytics from 'analytics/analytics'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ChatMapWidget from 'tripPlanner/components/Chat/ChatMapWidget'
import ChatMapFilters from 'tripPlanner/components/Chat/ChatMapFilters'
import ChatMapOffersDrawer from '../components/ChatMapOffersDrawer'
import {
  DEFAULT_MAP_STYLE_MODE,
  type MapPitchMode,
  type MapStyleMode,
} from 'tripPlanner/components/ChatMap/map3DConfig'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'
import type { MapCameraPosition, OfferMapSuggestionData, TripStop } from 'tripPlanner/api/types'
import type { RightPanelView } from '../hooks/useRightPanelState'
import { useSearchResults } from '../hooks/useSearchResults'
import { useMapFilterState } from '../hooks/useMapFilterState'
import { useAICameraState } from '../hooks/useAICameraState'
import { useMapData } from '../hooks/useMapData'
import {
  createOfferSuggestionMarker,
  isValidCoordinate,
  MARKER_SOURCE_ITINERARY,
  MARKER_TYPE_CHAT_EXPERIENCE,
  MARKER_TYPE_CHAT_HOTEL,
  type MapMarkerComponent,
  type MapCoordinate,
} from 'tripPlanner/utils/mapMarkerTransforms'
import { useEventSubscription, useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useChatUiAction } from '../contexts/ChatUiActionContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useMapInteraction } from '../contexts/MapInteractionContext'
import { useCruiseTransportMapMarkers } from '../hooks/useCruiseTransportMapMarkers'
import { trackMapPinClicked } from 'tripPlanner/utils/chatAnalytics'
import { getOfferItineraryDayRange } from 'tripPlanner/utils/chatItinerary'
import { isExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import type { LEExploreCategorySource } from 'tripPlanner/pages/chat/chatExploreCategories'
import {
  FullHeightMapWrapper,
  MapArea,
  MapOverlayStack,
  MapUpdatingIndicatorPill,
  SearchThisAreaButton,
  SHOW_ON_MAP_ZOOM,
} from '../styles/layoutStyles'

interface Props {
  /** When false, hooks still run (preserving state) but JSX returns null */
  visible: boolean
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
  selectedMarkerData: MarkerSelectionData | undefined
  onMarkerSelect: (data: MarkerSelectionData | undefined) => void
  setRightPanelView: Dispatch<SetStateAction<RightPanelView>>
  rightPanelView: RightPanelView
}

function getMarkerIdentityKey(marker: MapMarkerComponent) {
  return marker.offerId ?? marker.markerId ?? `${marker.lat}:${marker.lng}:${marker.label ?? ''}`
}

const EMPTY_FIT_BOUNDS_REQUEST: { coordinates: Array<MapCoordinate>; key: number } = {
  coordinates: [],
  key: 0,
}

function MapContainer({
  visible,
  selectedDayItineraryId,
  selectedStop,
  selectedMarkerData,
  onMarkerSelect,
  setRightPanelView,
  rightPanelView,
}: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const [offerSuggestionMarker, setOfferSuggestionMarker] = useState<MapMarkerComponent | undefined>(undefined)
  const [fitBoundsRequest, setFitBoundsRequest] = useState<{
    coordinates: Array<MapCoordinate>
    key: number
  }>(EMPTY_FIT_BOUNDS_REQUEST)
  const [lastMapCamera, setLastMapCamera] = useState<MapCameraPosition | undefined>(undefined)
  const [mapPitchMode, setMapPitchMode] = useState<MapPitchMode>('2d')
  const [mapStyleMode, setMapStyleMode] = useState<MapStyleMode>(DEFAULT_MAP_STYLE_MODE)
  const shouldSnapshotNextMapCameraRef = useRef(false)

  // ── Context reads ──

  const { sendMessage } = useChatActions()
  const { chatId, chatKey, isAwaitingMessageEnd, loadingState } = useChatStreamStatus()
  const { pendingUiAction, clearPendingUiAction } = useChatUiAction()
  const { messages } = useChatStreamMessages()
  const { itineraryData, enrichingStopIds, enrichingItemIds } = useItineraryContext()
  const { onMarkerHover, onCardHover, highlightedMarkerId } = useMapInteraction()

  // ── Hotel and experience search results ──

  const {
    searchQuery: hotelSearchQuery,
    hotelMapMarkers,
    isHotelFilterVisible,
    isHotelSearchActive,
    setHotelSearchResults,
    setHotelSearchError,
    setHotelSearchResultsFromCarousel,
    hideHotelFilter,
    resetHotelSearch,
    experienceSearchQuery,
    experienceMapMarkers,
    isExperienceFilterVisible,
    isExperienceSearchActive,
    setExperienceSearchResults,
    setExperienceSearchError,
    setExperienceSearchResultsFromCarousel,
    hideExperienceFilter,
    resetExperienceSearch,
  } = useSearchResults()

  // ── Map filter state ──

  const {
    handleMapViewStateChange,
    handleMapBoundsChange,
    handleHotelFilterDismiss,
    handleExperienceFilterDismiss,
    handleExploreCategorySelect,
    applyHotelFilterState,
    applyExperienceFilterState,
    handleExploreMapFromDrawer,
    handleSearchThisAreaClick,
    shouldShowSearchThisArea,
    isSearchThisAreaDisabled,
    deemphasizeBaseMarkers,
    disableClustering,
    selectedExploreCategory,
    exploreMarkers,
    exploreOffers,
    isExploreFilterVisible,
    isExploreFilterActive,
    isExploreLoading,
    isExploreOffersError,
    refetchExploreOffers,
    hasExploreResults,
    isBoundsDirty,
    resetFilterState,
  } = useMapFilterState({
    setRightPanelView,
    isHotelFilterVisible: isHotelSearchActive,
    hideHotelFilter,
    setHotelSearchResultsFromCarousel,
    isExperienceFilterVisible: isExperienceSearchActive,
    hideExperienceFilter,
    setExperienceSearchResultsFromCarousel,
  })

  const selectedExploreSource = selectedExploreCategory?.source
  const chatMapOffersDrawerType = selectedExploreSource === 'le-hotels'
    ? 'hotel'
    : selectedExploreSource === 'le-experiences'
      ? 'experience'
      : undefined
  const isChatMapOffersDrawerOpen = isExploreFilterVisible &&
    chatMapOffersDrawerType !== undefined &&
    (hasExploreResults || isExploreLoading || isExploreOffersError)

  // ── AI camera state ──

  const {
    aiCamera,
    aiCameraKey,
    moveMapTo,
    applyUiAction,
    handleAiCameraClear,
    resetCameraState,
  } = useAICameraState({ setRightPanelView })

  // ── Callbacks ──

  const clearFitBoundsRequest = useCallback(() => {
    setFitBoundsRequest(EMPTY_FIT_BOUNDS_REQUEST)
  }, [])

  const clearOfferSuggestionMapState = useCallback(() => {
    setOfferSuggestionMarker(undefined)
    clearFitBoundsRequest()
  }, [clearFitBoundsRequest])

  const handleMapViewStateChangeWithCameraSnapshot = useCallback((viewState: Parameters<typeof handleMapViewStateChange>[0]) => {
    handleMapViewStateChange(viewState)
    if (!shouldSnapshotNextMapCameraRef.current) return
    shouldSnapshotNextMapCameraRef.current = false
    setLastMapCamera({
      lat: viewState.latitude,
      lng: viewState.longitude,
      zoom: viewState.zoom,
    })
  }, [handleMapViewStateChange])

  const handleMapUserMoveStart = useCallback(() => {
    shouldSnapshotNextMapCameraRef.current = true
  }, [])

  const requestFitToCoordinates = useCallback((coordinates: Array<MapCoordinate>) => {
    const validCoordinates = coordinates.filter(isValidCoordinate)
    if (validCoordinates.length === 0) {
      clearFitBoundsRequest()
      return
    }
    setFitBoundsRequest(prev => ({
      coordinates: validCoordinates,
      key: prev.key + 1,
    }))
  }, [clearFitBoundsRequest])

  const requestFitToOfferCoordinates = useCallback((offers: Array<{ coordinates?: MapCoordinate }>) => {
    const coordinates = offers.flatMap((offer) => isValidCoordinate(offer.coordinates) ? [offer.coordinates] : [])
    if (coordinates.length === 0) {
      clearFitBoundsRequest()
      return
    }
    requestFitToCoordinates(coordinates)
  }, [clearFitBoundsRequest, requestFitToCoordinates])

  const {
    cruiseTransportMarkers,
    clearCruiseTransportMapState,
    handleCruiseTransportMapClick,
  } = useCruiseTransportMapMarkers({
    clearTransientMapState: clearOfferSuggestionMapState,
    hideHotelFilter,
    hideExperienceFilter,
    handleExploreCategorySelect,
    setRightPanelView,
    moveMapTo,
    requestFitToCoordinates,
    clearFitBoundsRequest,
  })

  const clearTransientMapState = useCallback(() => {
    clearOfferSuggestionMapState()
    clearCruiseTransportMapState()
  }, [clearCruiseTransportMapState, clearOfferSuggestionMapState])

  const handleExploreMapClick = useCallback((coordinates: { lat: number; lng: number }, source: LEExploreCategorySource) => {
    clearTransientMapState()
    moveMapTo(coordinates.lat, coordinates.lng, SHOW_ON_MAP_ZOOM)
    handleExploreMapFromDrawer(source)
  }, [clearTransientMapState, moveMapTo, handleExploreMapFromDrawer])

  const handleHotelMapViewClick = useCallback((data: Parameters<typeof applyHotelFilterState>[0]) => {
    clearTransientMapState()
    applyHotelFilterState(data)
    requestFitToOfferCoordinates(data.offers)
  }, [applyHotelFilterState, clearTransientMapState, requestFitToOfferCoordinates])

  const handleShowExperienceOnMap = useCallback((data: Parameters<typeof applyExperienceFilterState>[0]) => {
    clearTransientMapState()
    applyExperienceFilterState(data)
    requestFitToOfferCoordinates(data.offers)
  }, [applyExperienceFilterState, clearTransientMapState, requestFitToOfferCoordinates])

  const handleOfferSuggestionMapClick = useCallback((data: OfferMapSuggestionData) => {
    hideHotelFilter()
    hideExperienceFilter()
    handleExploreCategorySelect(undefined)
    clearCruiseTransportMapState()
    setRightPanelView('map')
    const suggestionMarker = createOfferSuggestionMarker(data)
    const isInItinerary = Boolean(getOfferItineraryDayRange(data.offerId, itineraryData))
    setOfferSuggestionMarker(
      isInItinerary
        ? {
            ...suggestionMarker,
            markerType: data.offerType === 'experience' ? MARKER_TYPE_CHAT_EXPERIENCE : MARKER_TYPE_CHAT_HOTEL,
            source: MARKER_SOURCE_ITINERARY,
          }
        : suggestionMarker,
    )
    moveMapTo(data.coordinates.lat, data.coordinates.lng, SHOW_ON_MAP_ZOOM)
  }, [hideHotelFilter, hideExperienceFilter, handleExploreCategorySelect, clearCruiseTransportMapState, setRightPanelView, moveMapTo, itineraryData])

  const handleDeselectExploreCategory = useCallback(() => {
    handleExploreCategorySelect(undefined)
  }, [handleExploreCategorySelect])

  const handleOfferClick = useCallback((data: { offerId: string; offerType?: string }) => {
    if (chatId) {
      trackMapPinClicked(chatId, data.offerId)
    }
    eventBus.dispatch({ type: 'open_offer_drawer', offerId: data.offerId, offerType: data.offerType })
  }, [eventBus, chatId])

  const handleExploreOfferClick = useCallback((data: { offerId: string; offerType?: string }) => {
    handleOfferClick(data)
  }, [handleOfferClick])

  // ── Deselect explore on view change away from map ──

  const prevRightPanelViewRef = useRef(rightPanelView)
  useEffect(() => {
    const prev = prevRightPanelViewRef.current
    prevRightPanelViewRef.current = rightPanelView
    if (prev === 'map' && rightPanelView !== 'map') {
      handleDeselectExploreCategory()
    }
  }, [rightPanelView, handleDeselectExploreCategory])

  // ── Event bus subscriptions ──

  useEventSubscription('hotel_search_results', (event) => {
    const accepted = setHotelSearchResults(event.data, event.generation)
    if (!accepted) return

    clearTransientMapState()
    hideExperienceFilter()
    if (event.data.offers.length === 0) {
      Analytics.trackClientEvent({
        subject: `${event.data.searchQuery}|chatId:${chatId}`,
        action: 'zero_search_results',
        category: 'chat_trip_planner',
        type: 'operational',
      })
    }
  })

  useEventSubscription('hotel_search_error', (event) => {
    const accepted = setHotelSearchError(event.data.message, event.generation, event.data.hotelSearchCallId)
    if (!accepted) return

    clearCruiseTransportMapState()
  })

  useEventSubscription('experience_search_results', (event) => {
    const accepted = setExperienceSearchResults(event.data, event.generation)
    if (!accepted) return

    clearTransientMapState()
    hideHotelFilter()
  })

  useEventSubscription('experience_search_error', (event) => {
    const accepted = setExperienceSearchError(event.data.message, event.generation, event.data.searchCallId)
    if (!accepted) return

    clearCruiseTransportMapState()
  })

  useEventSubscription('ui_action', (event) => {
    clearCruiseTransportMapState()
    applyUiAction(event.data)
    clearPendingUiAction()
  })

  // Apply buffered UI action that arrived before this component mounted
  // (e.g., during the landing→chat transition on first message)
  useEffect(() => {
    if (pendingUiAction) {
      clearCruiseTransportMapState()
      applyUiAction(pendingUiAction)
      clearPendingUiAction()
    }
  }, [pendingUiAction, applyUiAction, clearCruiseTransportMapState, clearPendingUiAction])

  useEventSubscription('show_hotel_on_map', (event) => {
    handleHotelMapViewClick(event.data)
  })

  useEventSubscription('show_experience_on_map', (event) => {
    handleShowExperienceOnMap(event.data)
  })

  useEventSubscription('show_offer_suggestion_on_map', (event) => {
    handleOfferSuggestionMapClick(event.data)
  })

  useEventSubscription('show_cruise_transport_on_map', (event) => {
    handleCruiseTransportMapClick(event.points)
  })

  useEventSubscription('show_explore_on_map', (event) => {
    handleExploreMapClick(event.coordinates, event.source)
  })

  useEventSubscription('itinerary_changed', () => {
    hideHotelFilter()
    hideExperienceFilter()
    resetFilterState()
    clearCruiseTransportMapState()
  })

  useEventSubscription('reset_chat_state', () => {
    resetHotelSearch()
    resetExperienceSearch()
    resetFilterState()
    resetCameraState()
    shouldSnapshotNextMapCameraRef.current = false
    setLastMapCamera(undefined)
    setMapPitchMode('2d')
    setMapStyleMode(DEFAULT_MAP_STYLE_MODE)
    clearTransientMapState()
  })

  // ── Map data + marker merging ──

  const { mapData: baseMapData, hasMapMarkers: hasBaseMapMarkers } = useMapData({
    messages,
    itineraryData,
    selectedDayItineraryId,
    selectedStop,
  })

  const isMapUpdating =
    isExploreLoading ||
    enrichingStopIds.size > 0 || enrichingItemIds.size > 0 ||
    loadingState.isContentStreaming || isAwaitingMessageEnd

  const itineraryExperienceMarkers = useMemo(() => {
    return (baseMapData.markers ?? []).filter(marker =>
      marker.source === MARKER_SOURCE_ITINERARY && marker.markerType === MARKER_TYPE_CHAT_EXPERIENCE,
    )
  }, [baseMapData.markers])

  const itineraryExperienceOfferIds = useMemo(() => {
    const offerIds = new Set<string>()

    itineraryData?.stops.forEach((stop) => {
      stop.days.forEach((day) => {
        day.items.forEach((item) => {
          if (isExperienceSavedData(item.savedItemData)) {
            offerIds.add(item.savedItemData.experienceId)
          }
        })
      })

      stop.itemsWithoutDay?.forEach((item) => {
        if (isExperienceSavedData(item.savedItemData)) {
          offerIds.add(item.savedItemData.experienceId)
        }
      })
    })

    return offerIds
  }, [itineraryData])

  const ownedExploreExperienceMarkers = useMemo(() => {
    return exploreMarkers.map((marker) => {
      if (marker.markerType !== 'explore-experience') {
        return marker
      }

      const matchingItineraryMarker = itineraryExperienceMarkers.find((itineraryMarker) => {
        return marker.offerId && itineraryMarker.offerId === marker.offerId
      })

      if (!matchingItineraryMarker && (!marker.offerId || !itineraryExperienceOfferIds.has(marker.offerId))) {
        return marker
      }

      return {
        ...marker,
        markerId: matchingItineraryMarker?.markerId ?? marker.markerId,
        offerId: matchingItineraryMarker?.offerId ?? marker.offerId,
        markerType: MARKER_TYPE_CHAT_EXPERIENCE,
        source: MARKER_SOURCE_ITINERARY,
      }
    })
  }, [exploreMarkers, itineraryExperienceMarkers, itineraryExperienceOfferIds])

  const mergedMarkers = useMemo(() => {
    const hasFilterResults =
      (isHotelSearchActive && hotelMapMarkers.length > 0) ||
      (isExperienceSearchActive && experienceMapMarkers.length > 0) ||
      (isExploreFilterActive && exploreMarkers.length > 0)
    // Only hide suggested markers when filter results are ready to replace them.
    // During the loading gap, suggested markers stay visible to preserve the
    // viewport and prevent useMapGlobeRotation from jumping to the default view.
    const base = hasFilterResults
      ? (baseMapData.markers ?? []).filter(m => m.source !== 'suggested')
      : (baseMapData.markers ?? [])
    // Priority: hotel > experience > explore > base
    if (isHotelSearchActive && hotelMapMarkers.length > 0) return [...hotelMapMarkers, ...base]
    if (isExperienceSearchActive && experienceMapMarkers.length > 0) return [...experienceMapMarkers, ...base]
    if (isExploreFilterActive && exploreMarkers.length > 0) {
      const ownedMarkerKeys = new Set(
        ownedExploreExperienceMarkers
          .filter(marker => marker.source === MARKER_SOURCE_ITINERARY)
          .map(getMarkerIdentityKey),
      )
      const dedupedBase = base.filter(marker => !ownedMarkerKeys.has(getMarkerIdentityKey(marker)))
      const result = [...ownedExploreExperienceMarkers, ...dedupedBase]
      return result
    }
    if (offerSuggestionMarker) {
      const hasOwnedMarker = base.some(marker => marker.offerId === offerSuggestionMarker.offerId)
      return hasOwnedMarker ? base : [offerSuggestionMarker, ...base]
    }
    if (cruiseTransportMarkers.length > 0) return [...cruiseTransportMarkers, ...base]
    return base
  }, [
    baseMapData.markers,
    hotelMapMarkers,
    experienceMapMarkers,
    exploreMarkers,
    ownedExploreExperienceMarkers,
    isHotelSearchActive,
    isExperienceSearchActive,
    isExploreFilterActive,
    offerSuggestionMarker,
    cruiseTransportMarkers,
  ])

  const mapData = useMemo(() => ({
    ...baseMapData,
    markers: mergedMarkers,
  }), [baseMapData, mergedMarkers])

  const hasMapMarkers = hasBaseMapMarkers ||
    (isHotelSearchActive && hotelMapMarkers.length > 0) ||
    (isExperienceSearchActive && experienceMapMarkers.length > 0) ||
    (isExploreFilterActive && exploreMarkers.length > 0) ||
    Boolean(offerSuggestionMarker) ||
    cruiseTransportMarkers.length > 0

  const shouldResetToGlobe = !hasMapMarkers && !aiCamera && !lastMapCamera

  // ── i18n ──

  const searchThisAreaLabel = intl.formatMessage({
    id: 'kVSN4s5ZBcxyXhOZlUJO',
    defaultMessage: 'Search this area',
    description: 'Button label to search for offers in the currently visible map area',
  })

  // Convention exception: "Components should not determine whether they render"
  // This container must stay mounted so hooks (useSearchResults, useMapFilterState,
  // useAICameraState) preserve state across Map↔Itinerary view toggles. The parent
  // controls visibility via the `visible` prop; the child only gates JSX output.
  if (!visible) return null

  return (
    <FullHeightMapWrapper>
      <MapArea>
        <MapOverlayStack>
          <ChatMapFilters
            hotelSearchQuery={hotelSearchQuery}
            isHotelFilterVisible={isHotelFilterVisible}
            onHotelFilterDismiss={handleHotelFilterDismiss}
            experienceSearchQuery={experienceSearchQuery}
            isExperienceFilterVisible={isExperienceFilterVisible}
            onExperienceFilterDismiss={handleExperienceFilterDismiss}
            isExploreFiltersVisible={isExploreFilterVisible}
            isExploreFilterActive={isExploreFilterActive}
            selectedExploreCategory={selectedExploreCategory}
            onExploreCategorySelect={handleExploreCategorySelect}
            isExploreLoading={isExploreLoading}
            hasExploreResults={hasExploreResults}
            isBoundsDirty={isBoundsDirty}
          />
          {shouldShowSearchThisArea && (
            <SearchThisAreaButton
              kind="secondary"
              variant="dark"
              size="small"
              shape="round"
              startIcon={<LineSearchIcon />}
              onClick={handleSearchThisAreaClick}
              disabled={isSearchThisAreaDisabled}
            >
              {searchThisAreaLabel}
            </SearchThisAreaButton>
          )}
          {isMapUpdating && (
            <MapUpdatingIndicatorPill>
              <LoadingAnimation size={8} />
            </MapUpdatingIndicatorPill>
          )}
        </MapOverlayStack>
        <ChatMapWidget
          key="trip-planner-map"
          chatKey={chatKey}
          data={mapData}
          fillContainer
          onMarkerSelect={onMarkerSelect}
          highlightedMarkerId={highlightedMarkerId}
          onViewStateChange={handleMapViewStateChangeWithCameraSnapshot}
          onUserMoveStart={handleMapUserMoveStart}
          onBoundsChange={handleMapBoundsChange}
          isDrawerOpen={Boolean(selectedMarkerData)}
          shouldResetToGlobe={shouldResetToGlobe}
          deemphasizeBaseMarkers={deemphasizeBaseMarkers}
          exploreOffers={exploreOffers}
          disableClustering={disableClustering}
          onMarkerHover={onMarkerHover}
          onOfferClick={handleOfferClick}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          initialCamera={lastMapCamera}
          aiCamera={aiCamera}
          aiCameraKey={aiCameraKey}
          fitBoundsCoordinates={fitBoundsRequest.coordinates}
          fitBoundsKey={fitBoundsRequest.key}
          onFitBoundsHandled={clearFitBoundsRequest}
          onAiCameraClear={handleAiCameraClear}
          mapPitchMode={mapPitchMode}
          onMapPitchModeChange={setMapPitchMode}
          mapStyleMode={mapStyleMode}
          onMapStyleModeChange={setMapStyleMode}
        />
      </MapArea>
      {chatMapOffersDrawerType && (
        <ChatMapOffersDrawer
          isOpen={isChatMapOffersDrawerOpen}
          onClose={handleDeselectExploreCategory}
          offers={exploreOffers}
          offerType={chatMapOffersDrawerType}
          isLoading={isExploreLoading}
          isError={isExploreOffersError}
          onRetry={refetchExploreOffers}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          onViewOffer={handleExploreOfferClick}
          highlightedMarkerId={highlightedMarkerId}
          onCardHover={onCardHover}
        />
      )}
    </FullHeightMapWrapper>
  )
}

export default MapContainer
